import { useRef, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@monksflow/monks-ui';

import { useMountEffect } from '@/shared/hooks/useMountEffect';

import { buildDisplaySteps } from '../../lib/build-display-steps';
import { useBriefGeneration } from '../../model/useBriefGeneration';
import type {
  GenerationStatus,
  PipelineStepId,
  PipelineStepState,
} from '../../model/brief-generation.types';

import { BriefGenerationStep } from '../BriefGenerationStep';
import { BriefGenerationStream } from '../BriefGenerationStream';
import type { BriefGenerationProgressModalProps } from './BriefGenerationProgressModal.types';
import { useTranslation } from 'react-i18next';

const IN_FLIGHT_STATUSES: ReadonlySet<GenerationStatus> = new Set([
  'preparing',
  'uploading',
  'streaming',
  'persisting',
]);

const deriveDisplayStepState = (
  steps: Map<PipelineStepId, PipelineStepState>,
  ids: readonly PipelineStepId[],
): PipelineStepState => {
  const states = ids.map((id) => steps.get(id) ?? 'pending');
  if (states.includes('error')) return 'error';
  if (states.includes('running')) return 'running';
  if (states.every((state) => state === 'done')) return 'done';
  return 'pending';
};

export const BriefGenerationProgressModal = ({
  open,
  onClose,
  projectId,
  payload,
  onSuccess,
}: BriefGenerationProgressModalProps) => {
  const { t } = useTranslation('briefs');

  const hook = useBriefGeneration({ projectId, onSuccess });
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const statusRef = useRef(hook.status);
  const cancelRef = useRef(hook.cancel);
  statusRef.current = hook.status;
  cancelRef.current = hook.cancel;
  const stepStateById = new Map(hook.steps.map((step) => [step.id, step.state]));
  const savingBriefState = stepStateById.get('saving-brief') ?? 'pending';
  const displaySteps = buildDisplaySteps(payload).map((step) => ({
    ...step,
    state: deriveDisplayStepState(stepStateById, step.pipelineIds),
  }));

  useMountEffect(() => {
    const startTimer = window.setTimeout(() => {
      if (payload) void hook.start(payload);
    }, 0);

    return () => {
      window.clearTimeout(startTimer);
      if (IN_FLIGHT_STATUSES.has(statusRef.current)) {
        cancelRef.current();
      }
    };
  });

  const handleOpenChange = (next: boolean) => {
    if (next) return;
    if (IN_FLIGHT_STATUSES.has(hook.status)) {
      setShowCancelConfirm(true);
      return;
    }
    onClose();
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    hook.cancel();
    onClose();
  };

  const handleKeepGenerating = () => {
    setShowCancelConfirm(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          aria-describedby={undefined}
          className="flex h-[622px] w-full max-w-160 flex-col gap-0 overflow-hidden rounded-[10px] border border-border bg-background p-0 text-foreground shadow-sm [&>button]:right-4 [&>button]:top-4 [&>button]:rounded-none [&>button]:border-0 [&>button]:bg-transparent [&>button]:p-0 [&>button]:text-muted-foreground [&>button]:shadow-none"
          onPointerDownOutside={(event) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest('[class*="notistack"]')) {
              event.preventDefault();
            }
          }}
        >
          <DialogHeader className="p-4">
            <DialogTitle className="text-xl font-medium leading-6 tracking-[-0.05em] text-foreground">
              {t('briefGeneration.briefGenerationProgressModal.newBrief')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="flex min-h-0 flex-1 bg-background p-0">
            <div className="flex flex-1 flex-col items-center gap-6 px-0 py-10">
              <div className="flex flex-col items-center gap-6">
                <Spinner
                  className="size-12 text-primary"
                  data-testid="brief-generation-hero-spinner"
                  aria-hidden="true"
                />
                <h2 className="text-xl font-medium leading-6 tracking-[-0.05em] text-foreground">
                  {t('briefGeneration.briefGenerationProgressModal.generatingBrief')}
                </h2>
              </div>
              <ul className="flex w-[418px] max-w-[calc(100%-32px)] flex-1 flex-col justify-center gap-2">
                {displaySteps.map((step) => (
                  <BriefGenerationStep
                    key={step.id}
                    label={step.label}
                    state={step.state}
                    retrySlot={
                      step.id === 'generate-brief' && savingBriefState === 'error' ? (
                        <Button size="sm" variant="outline" onClick={hook.retry}>
                          {t('briefGeneration.briefGenerationProgressModal.retry')}
                        </Button>
                      ) : undefined
                    }
                  />
                ))}
              </ul>
            </div>
          </DialogBody>
          {hook.streamProps && <BriefGenerationStream {...hook.streamProps} />}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={showCancelConfirm}
        onOpenChange={(next) => !next && handleKeepGenerating()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('briefGeneration.briefGenerationProgressModal.cancelGeneration')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('briefGeneration.briefGenerationProgressModal.generationIsInProgressIf')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleKeepGenerating}>
              {t('briefGeneration.briefGenerationProgressModal.keepGenerating')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel} variant="destructive">
              {t('briefGeneration.briefGenerationProgressModal.cancelGeneration2')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
